import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { hasActionAccess } from '../../../utils/roleBasedAccess';

const CandidateCards = ({ 
  candidates, 
  selectedCandidates, 
  onSelectionChange, 
  onCandidateClick, 
  onQuickAction,
  userRole
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'Plus' },
      'screening': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: 'Clock' },
      'interview': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'Calendar' },
      'offer': { color: 'bg-green-100 text-green-800 border-green-200', icon: 'CheckCircle' },
      'rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: 'X' },
      'hired': { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: 'UserCheck' }
    };

    const config = statusConfig?.[status] || statusConfig?.['new'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getScoreBadge = (score) => {
    let colorClass = 'bg-gray-100 text-gray-800 border-gray-200';
    
    if (score >= 90) colorClass = 'bg-green-100 text-green-800 border-green-200';
    else if (score >= 80) colorClass = 'bg-blue-100 text-blue-800 border-blue-200';
    else if (score >= 70) colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';
    else if (score >= 60) colorClass = 'bg-orange-100 text-orange-800 border-orange-200';
    else colorClass = 'bg-red-100 text-red-800 border-red-200';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        {score}%
      </span>
    );
  };

  const handleSelectCandidate = (candidateId, checked) => {
    if (checked) {
      onSelectionChange([...selectedCandidates, candidateId]);
    } else {
      onSelectionChange(selectedCandidates?.filter(id => id !== candidateId));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {candidates?.map((candidate) => (
        <div
          key={candidate?.id}
          className={`bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200 cursor-pointer card-elevation-base card-elevation-hover ${
            selectedCandidates?.includes(candidate?.id) ? 'ring-2 ring-primary/20 bg-primary/5' : ''
          }`}
          onClick={() => onCandidateClick(candidate)}
        >
          {/* Header with checkbox and status */}
          <div className="flex items-start justify-between mb-4" onClick={(e) => e?.stopPropagation()}>
            <Checkbox
              checked={selectedCandidates?.includes(candidate?.id)}
              onChange={(e) => handleSelectCandidate(candidate?.id, e?.target?.checked)}
            />
            {getStatusBadge(candidate?.status)}
          </div>

          {/* Candidate Info */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={candidate?.avatar}
                alt={candidate?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate">{candidate?.name}</h3>
              <p className="text-sm text-muted-foreground truncate">{candidate?.email}</p>
              <div className="flex items-center mt-1">
                <Icon name="MapPin" size={12} className="text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground">{candidate?.location}</span>
              </div>
            </div>
          </div>

          {/* Position and Department */}
          <div className="mb-4">
            <h4 className="font-medium text-foreground">{candidate?.position}</h4>
            <p className="text-sm text-muted-foreground">{candidate?.department}</p>
          </div>

          {/* AI Score and Experience */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">AI Score:</span>
              {getScoreBadge(candidate?.aiScore)}
            </div>
            <div className="text-sm text-muted-foreground">
              {candidate?.experience} years exp.
            </div>
          </div>

          {/* Application Date */}
          <div className="flex items-center text-sm text-muted-foreground mb-4">
            <Icon name="Calendar" size={14} className="mr-2" />
            <span>Applied {formatDate(candidate?.applicationDate)}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2" onClick={(e) => e?.stopPropagation()}>
            {/* View Profile - Available to all roles */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onQuickAction('view', candidate)}
              iconName="Eye"
              iconSize={14}
              className="flex-1"
            >
              View
            </Button>
            
            {/* Schedule Interview - Only for roles with interview scheduling access */}
            {hasActionAccess(userRole, 'interviews', 'schedule') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onQuickAction('interview', candidate)}
                iconName="Calendar"
                iconSize={14}
              >
                Interview
              </Button>
            )}
            
            {/* Send Message - Available to all roles except hr_user */}
            {userRole !== 'hr_user' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onQuickAction('message', candidate)}
                iconName="MessageCircle"
                iconSize={14}
              >
                Message
              </Button>
            )}
          </div>
        </div>
      ))}
      {candidates?.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No candidates found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or search criteria to find candidates.
          </p>
        </div>
      )}
    </div>
  );
};

export default CandidateCards;